import { useEffect, useState } from "react";
import { Button, ErrorAlert, Header, LinkBtn } from "@";
import { useProjectForm } from "../../hooks/index";
import CustomerCampaignInfo from "../../components/CustomerCampaignInfo";
import {
  AdahiSection,
  BankAccountsSection,
  ProjectAttributesSection,
  ProjectDetailsSection,
  ProjectInfoSection,
  ProjectLicenceSection,
  ProjectSettingsSection,
  SeoSettingsSection,
  ThemeSelectionSection,
} from "../../components/form-sections";
import { ProjectType } from "@/features/donations/projects/types.ts";
import { VerificationsSection } from "@/features/donations/projects/components/form-sections/verifications-section.tsx";
import { EndowmentCertificateDesignSection } from "@/features/donations/projects/components/form-sections/endowment-certificate-design-section.tsx";
import {
  FormTabs,
  findFirstErrorTab,
  type FormTabId,
} from "../../components/FormTabs";

const Page = () => {
  const {
    // Form
    register,
    watch,
    setValue,
    getValues,
    control,
    errors,
    handleSubmit,
    isSubmitting,
    isSubmitSuccessful,

    // Route params
    edit,
    show,

    // Data
    giftProjects,

    // Options
    categoriesOptions,
    adahiProductsOptions,
    adahiRitualOptions,
    accountsOptions,

    // Error & Links
    error,
    links,

    // Customer Campaign
    customerCampaign,

    // Handlers
    submit,
    refetchAll,
    handleUpload,
    handleUploadCertificate,
    handleSelect,
    checkOnSelectedAccounts,

    // Certificate Design
    saveCertificateImageDesign,
    showCertificateImageDesigner,
    isShowCertificateImageDesigner,
    certificateImageDesign,
    closeCertificateImageDesigner,
  } = useProjectForm();

  const [activeTab, setActiveTab] = useState<FormTabId>("basics");

  // Jump to the first tab containing a validation error after a failed submit.
  useEffect(() => {
    if (!isSubmitting && Object.keys(errors || {}).length > 0) {
      const firstErrorTab = findFirstErrorTab(errors);
      if (firstErrorTab) setActiveTab(firstErrorTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting]);

  // Shared props for all sections
  const formProps = {
    register,
    watch,
    setValue,
    getValues,
    control,
    errors,
    show,
    isSubmitSuccessful,
  };

  const tabPanelClass = (id: FormTabId) =>
    `${
      activeTab === id ? "grid" : "hidden"
    } animate-[fadeIn_.25s_ease-out] gap-x-5 lg:grid-cols-2`;

  return (
    <>
      {/* error alert */}
      <ErrorAlert reload={refetchAll} show={error} />

      {/* header */}
      <Header
        flex
        title={`${
          edit ? "تعديل المشروع" : show ? "عرض المشروع" : "اضافة مشروع"
        }`}
        links={links}
      >
        {show && <LinkBtn to={`/projects/${show}/edit`} edit />}
      </Header>

      <CustomerCampaignInfo
        customerCampaign={
          customerCampaign || {
            projectTitle: getValues("customerSuggestedCampaignName") as string,
            userName: getValues("customerSuggestedCampaignName") as string,
          }
        }
      />

      <form onSubmit={handleSubmit(submit)} className="relative">
        <FormTabs active={activeTab} onChange={setActiveTab} errors={errors} />

        {/* Tab: المعلومات الأساسية */}
        <div className={tabPanelClass("basics")} role="tabpanel">
          <ProjectInfoSection
            {...formProps}
            categoriesOptions={categoriesOptions}
            customerCampaign={customerCampaign}
          />
          <ProjectSettingsSection
            {...formProps}
            handleUpload={handleUpload}
            giftProjects={giftProjects}
            customerCampaign={customerCampaign}
          />
          {Number(watch("projectType")) !== ProjectType.Open && (
            <ProjectAttributesSection {...formProps} />
          )}
        </div>

        {/* Tab: المحتوى والوصف */}
        <div className={tabPanelClass("content")} role="tabpanel">
          <ProjectDetailsSection {...formProps} />
          <SeoSettingsSection {...formProps} />
        </div>

        {/* Tab: التراخيص والتوثيقات */}
        <div className={tabPanelClass("licences")} role="tabpanel">
          <VerificationsSection {...formProps} />
          <ProjectLicenceSection {...formProps} />
        </div>

        {/* Tab: الشهادات والقوالب */}
        <div className={tabPanelClass("design")} role="tabpanel">
          <EndowmentCertificateDesignSection
            {...formProps}
            customerCampaign={customerCampaign}
            handleUploadCertificate={handleUploadCertificate}
            isShowCertificateImageDesigner={isShowCertificateImageDesigner}
            saveCertificateImageDesign={saveCertificateImageDesign}
            closeCertificateImageDesigner={closeCertificateImageDesigner}
            certificateImageDesign={certificateImageDesign}
            showCertificateImageDesigner={showCertificateImageDesigner}
          />
          <ThemeSelectionSection
            watch={watch}
            setValue={setValue}
            getValues={getValues}
            show={show}
            register={register}
            errors={errors}
          />
        </div>

        {/* Tab: الحسابات والأضاحي */}
        <div className={tabPanelClass("financial")} role="tabpanel">
          <BankAccountsSection
            {...formProps}
            control={control}
            accountsOptions={accountsOptions}
            handleSelect={handleSelect}
            checkOnSelectedAccounts={checkOnSelectedAccounts}
            watch={watch}
          />
          <AdahiSection
            {...formProps}
            adahiProductsOptions={adahiProductsOptions}
            adahiRitualOptions={adahiRitualOptions}
          />
        </div>

        {/* Submit Button */}
        {!show && (
          <div className="floating-btn">
            <Button
              className="w-full"
              isLoading={isSubmitting}
              type="submit"
              title="حفظ البيانات المشروع"
            />
          </div>
        )}
      </form>
    </>
  );
};

export default Page;
